import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const LABELS = {
  students: "Student accounts", attempts: "Practice attempts", assignments: "Assignments",
  community_posts: "Community posts & replies", video_views: "Video views", payments: "Payment records",
  payment_failures: "Failed-payment records", cancellation_requests: "Cancellation requests",
  password_reset_tokens: "Password-reset links", demo_requests: "Demo-class requests",
};

export default function DeleteAllStudentsCard({ onDone }) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const openDialog = async () => {
    setOpen(true); setConfirm(""); setSummary(null);
    try { const { data } = await api.get("/admin/danger/student-data-summary"); setSummary(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/danger/delete-all-students", { confirm });
      toast.success(`Deleted ${data.deleted.students} students and their data. ${data.subscriptions_cancelled.length} auto-debit subscription(s) cancelled.`);
      if (data.subscriptions_cancel_failed.length) {
        toast.error(`Could not cancel ${data.subscriptions_cancel_failed.length} Razorpay subscription(s): ${data.subscriptions_cancel_failed.join(", ")}. Cancel them in the Razorpay dashboard.`, { duration: 20000 });
      }
      setOpen(false);
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-12 p-6 rounded-3xl border-2 border-watermelon/40 bg-watermelon-light/40" data-testid="danger-zone">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="font-heading text-lg font-bold text-watermelon flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Danger zone</div>
          <p className="text-sm text-stone">Permanently delete every student and all their data. Admins, teachers, exercises, videos and coupons are kept.</p>
        </div>
        <button data-testid="delete-all-students-open" onClick={openDialog} className="h-11 px-5 rounded-full bg-watermelon text-white font-bold inline-flex items-center gap-2 shrink-0 hover:bg-watermelon-hover">
          <Trash2 className="w-4 h-4" /> Delete all student data
        </button>
      </div>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent data-testid="delete-all-students-dialog">
          <DialogHeader>
            <DialogTitle className="text-watermelon">Delete all student data?</DialogTitle>
            <DialogDescription>This cannot be undone. Active Razorpay auto-debits are cancelled first so nobody keeps getting charged.</DialogDescription>
          </DialogHeader>
          {summary ? (
            <ul className="text-sm space-y-1" data-testid="delete-summary">
              {Object.entries(LABELS).map(([k, label]) => (
                <li key={k} className="flex justify-between"><span className="text-stone">{label}</span><span className="font-bold text-ink tabular-nums" data-testid={`delete-count-${k}`}>{summary[k] ?? 0}</span></li>
              ))}
            </ul>
          ) : <div className="text-sm text-stone">Counting records…</div>}
          <label className="block text-sm font-bold text-ink mt-2">Type <span className="font-mono text-watermelon">DELETE</span> to confirm</label>
          <input data-testid="delete-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-watermelon focus:outline-none font-mono" />
          <button data-testid="delete-all-students-confirm" onClick={run} disabled={confirm !== "DELETE" || busy || !summary} className="w-full h-12 rounded-full bg-watermelon text-white font-bold disabled:opacity-40">
            {busy ? "Deleting…" : "Permanently delete everything"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
